/**
 * 牌桌聊天气泡的排队规则（纯模型，时间由调用方传入）。
 *
 *  - 同一座位新话**顶掉**旧话，不排队；
 *  - 全桌同时最多 `SAY_MAX` 个，再来就让最老的先退场；
 *  - 同一座位的行动气泡开播时先**让位**，播完再回来；剩余停留不足 `RESUME_MIN_MS` 就不回来了
 *    （闪一下比不出现更干扰）；
 *  - 到期先进「退场」态（CSS 淡出 `LEAVE_MS`），再真正移除。
 */
import type { SeatIndex } from '@poker/shared'

import { sayHoldMs, type SayKind } from './talkDerive.js'

export const SAY_MAX = 4
export const RESUME_MIN_MS = 800
export const LEAVE_MS = 240

export type SayState = 'on' | 'yield' | 'leaving'

export interface SayBubble {
  readonly key: number
  readonly seat: SeatIndex
  readonly text: string
  readonly kind: SayKind
  readonly state: SayState
}

interface Entry {
  readonly key: number
  readonly seat: SeatIndex
  readonly text: string
  readonly kind: SayKind
  /** 停留到这一刻（退场态下 = 开始淡出的时刻） */
  until: number
  /** >0 = 正在让位，到这一刻为止 */
  yieldUntil: number
  leaving: boolean
}

export class SayBoard {
  private entries: Entry[] = []
  private seq = 0

  add(seat: SeatIndex, text: string, kind: SayKind, now: number): void {
    const kept = this.entries.filter((e) => e.seat !== seat)
    const live = kept.filter((e) => !e.leaving)
    if (live.length >= SAY_MAX) {
      const oldest = live[0]
      if (oldest !== undefined) {
        oldest.leaving = true
        oldest.yieldUntil = 0
        oldest.until = now
      }
    }
    kept.push({ key: ++this.seq, seat, text, kind, until: now + sayHoldMs(kind, text), yieldUntil: 0, leaving: false })
    this.entries = kept
  }

  /** 行动气泡在这个座位上开播 `ms`。有聊天气泡在播才算数，返回是否有变化 */
  yieldSeat(seat: SeatIndex, ms: number, now: number): boolean {
    const e = this.entries.find((x) => x.seat === seat && !x.leaving)
    if (e === undefined) return false
    e.yieldUntil = Math.max(e.yieldUntil, now + ms)
    return true
  }

  /** 推进到 `now`，返回是否有可见变化 */
  advance(now: number): boolean {
    let changed = false
    const next: Entry[] = []
    for (const e of this.entries) {
      if (e.leaving) {
        if (now >= e.until + LEAVE_MS) {
          changed = true
          continue
        }
      } else if (e.yieldUntil > 0) {
        if (now >= e.yieldUntil) {
          e.yieldUntil = 0
          changed = true
          if (e.until - now < RESUME_MIN_MS) continue
        }
      } else if (now >= e.until) {
        e.leaving = true
        changed = true
      }
      next.push(e)
    }
    this.entries = next
    return changed
  }

  /** 下一次需要 `advance` 的时刻；没有气泡时为 null */
  nextAt(): number | null {
    let soonest: number | null = null
    for (const e of this.entries) {
      const at = e.leaving ? e.until + LEAVE_MS : e.yieldUntil > 0 ? e.yieldUntil : e.until
      if (soonest === null || at < soonest) soonest = at
    }
    return soonest
  }

  bubbles(): SayBubble[] {
    return this.entries.map((e) => ({
      key: e.key,
      seat: e.seat,
      text: e.text,
      kind: e.kind,
      state: e.leaving ? 'leaving' : e.yieldUntil > 0 ? 'yield' : 'on',
    }))
  }

  clear(): void {
    this.entries = []
  }
}
